import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetExpenseReceiptAttachmentIds } from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import {
  selectDeletingExpenseFromState,
  selectErrorDetailMapFromState,
  selectExpenseIdFromRoute,
  selectExpenseLoadedFromState,
  selectExpenseModelFromState,
  selectExpenseReportModelFromState,
  selectPatchingExpenseFromState,
  selectReceiptAttachmentModelsList,
  selectTopLevelError,
  selectUploadingAttachmentsFromState,
} from 'src/app/UpdateExpenseFlyout/redux/updateExpense.selectors'
import ExpenseFlyoutHeader from 'src/components/ExpenseFlyoutHeader/ExpenseFlyoutHeader'
import ExpenseForm, {
  ExpenseFields,
} from 'src/components/ExpenseForm/ExpenseForm'
import LoadingRightFlyoutContent from 'src/components/LoadingRightFlyoutContent/LoadingRightFlyoutContent'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { ExtendCurrency } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { ExpenseReportStatus } from 'src/models/expenseReport.model'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { createSetIsDeleteExpensePopupOpen } from 'src/redux/actions/app.actions'
import {
  getExpense,
  getExpenseReport,
  patchExpenseAndOpenToastAndCloseFlyout,
} from 'src/redux/actions/data/expenses.actions'
import { uploadAttachments } from 'src/redux/actions/data/receiptAttachments.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOutOfPocketExpensesVisible,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toCents, toDollars } from 'src/utils/currency.utils'

const UpdatExpenseFlyout: React.FC = () => {
  const translate = useTranslate()

  const { show_mileage_route_map: mileageRouteMapEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}
  const expense = useSelector(selectExpenseModelFromState)
  const expenseReport = useSelector(selectExpenseReportModelFromState)
  const expenseId = useSelector(selectExpenseIdFromRoute)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const patchingExpense = useSelector(selectPatchingExpenseFromState)
  const deletingExpense = useSelector(selectDeletingExpenseFromState)
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const topLevelError = useSelector(selectTopLevelError)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const loaded = useSelector(selectExpenseLoadedFromState)
  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesVisible,
  )
  const user = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const isApproverOrSubmitter =
    user?.id === expenseReport?.employeeId ||
    user?.id === organization?.expenseApprover?.id

  const isSubmitter = user?.id === expenseReport?.employeeId
  const isApprover = user?.id === organization?.expenseApprover?.id

  const notesAndReceiptsEditable =
    (expenseReport?.status === ExpenseReportStatus.Pending ||
      expenseReport?.status === ExpenseReportStatus.Draft) &&
    isApproverOrSubmitter
  const fieldsEditable = expenseReport?.status === ExpenseReportStatus.Draft
  const hasViewOnlyPermission = user?.hasOrgBookkeeperPermissions

  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: user?.organizationId,
    userId: user?.id,
  })
  const isManager = directReports !== undefined && directReports.length > 0

  const dispatch = useFlyoutDispatch()
  const canViewExpenses =
    isApproverOrSubmitter || hasViewOnlyPermission || isManager

  const { analytics } = useAnalyticsContext()

  const baseFlatFields = useMemo(
    (): ExpenseFields => ({
      expenseName: expense?.expenseName,
      currencyCode: expense?.currencyCode,
      expenseReportId: expenseReport?.id,
      expenseDate:
        expense?.expenseDate !== undefined
          ? moment(expense.expenseDate)
          : undefined,
      amountCents:
        expense?.amountCents !== undefined
          ? toDollars(expense.amountCents)
          : undefined,
      notes: expense?.notes,
      type: expense?.type,
      distance: expense?.distance,
      mileageRate:
        expense?.mileageRate !== undefined
          ? expense?.mileageRate / 1000
          : undefined,
      ...(mileageRouteMapEnabled && {
        useMileageRoute: expense?.useMileageRoute,
        mileageRoute: expense?.mileageRoute,
      }),
    }),
    [expense, mileageRouteMapEnabled],
  )

  const negativeButton = {
    loading: deletingExpense,
    text: translate('Delete'),
    onClick: (): void => {
      dispatch(createSetIsDeleteExpensePopupOpen(true, [expenseId]))
    },
  }

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    dispatch(uploadAttachments(attachments))
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetExpenseReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const dispatchPatchExpense = (
    body: ExpenseFields,
    amountToBeReimbursed: number,
    isExpenseTypeMileage: boolean,
  ): void => {
    const trackUpdate = (): void => {
      const includeMethod = fieldsEditable && isExpenseTypeMileage
      analytics?.track('update reimbursement expense', {
        expense_type: body.type === 'MILEAGE' ? 'Mileage' : 'General',
        amount_spent: body.amountCents ? body.amountCents : undefined,
        distance: body.distance ?? body.mileageRoute?.distance,
        expense_name: expenseReport?.reportName,
        receipt_attached: receiptAttachments && receiptAttachments.length > 0,
        add_notes: !!body.notes,
        ...(includeMethod && {
          mileage_calculation_method: body.useMileageRoute
            ? 'google_maps'
            : 'manual',
        }),
        ...(includeMethod &&
          body.useMileageRoute && {
            stops_count: body.mileageRoute?.stops?.length ?? 0,
          }),
      })
    }

    if (!mileageRouteMapEnabled) {
      fieldsEditable
        ? dispatch(
            patchExpenseAndOpenToastAndCloseFlyout(
              expense?.id,
              {
                ...body,
                amountCents: isExpenseTypeMileage
                  ? toCents(amountToBeReimbursed)
                  : body.amountCents !== undefined
                    ? toCents(body.amountCents)
                    : 0,
                expenseReportId: expense?.expenseReportId,
                currencyCode: ExtendCurrency.Usd,
                receiptAttachmentIds: receiptAttachments.map((att) => att.id),
                ...(isExpenseTypeMileage && { useMileageRoute: false }),
              },
              analytics,
            ),
          )
        : dispatch(
            patchExpenseAndOpenToastAndCloseFlyout(
              expense?.id,
              {
                expenseReportId: expense?.expenseReportId,
                notes: body.notes !== undefined ? body.notes : undefined,
                receiptAttachmentIds: receiptAttachments.map((att) => att.id),
              },
              analytics,
            ),
          )
      trackUpdate()
      return
    }

    if (!fieldsEditable) {
      dispatch(
        patchExpenseAndOpenToastAndCloseFlyout(
          expense?.id,
          {
            expenseReportId: expense?.expenseReportId,
            notes: body.notes !== undefined ? body.notes : undefined,
            receiptAttachmentIds: receiptAttachments.map((att) => att.id),
          },
          analytics,
        ),
      )
      trackUpdate()
      return
    }

    const changedFields: Record<string, unknown> = {}

    if (body.expenseName !== baseFlatFields.expenseName) {
      changedFields.expenseName = body.expenseName
    }
    if (body.expenseDate?.valueOf() !== baseFlatFields.expenseDate?.valueOf()) {
      changedFields.expenseDate = body.expenseDate
    }
    if (body.notes !== baseFlatFields.notes) {
      changedFields.notes = body.notes
    }
    if (body.type !== baseFlatFields.type) {
      changedFields.type = body.type
    }

    const currentAttachmentIds = receiptAttachments.map((att) => att.id)
    const originalAttachmentIds =
      expense?.receiptAttachments?.map((att) => att.id) ?? []
    if (
      JSON.stringify(currentAttachmentIds) !==
      JSON.stringify(originalAttachmentIds)
    ) {
      changedFields.receiptAttachmentIds = currentAttachmentIds
    }

    if (isExpenseTypeMileage) {
      const newAmountCents = toCents(amountToBeReimbursed)
      if (newAmountCents !== expense?.amountCents) {
        changedFields.amountCents = newAmountCents
      }

      const bodyUseMileageRoute = body.useMileageRoute ?? true
      changedFields.useMileageRoute = bodyUseMileageRoute

      if (bodyUseMileageRoute) {
        changedFields.distance = null
        changedFields.mileageRoute = body.mileageRoute ?? null
      } else {
        changedFields.distance = body.distance
        changedFields.mileageRoute = null
      }
    } else {
      const newAmountCents =
        body.amountCents !== undefined ? toCents(body.amountCents) : 0
      if (newAmountCents !== expense?.amountCents) {
        changedFields.amountCents = newAmountCents
      }
    }

    dispatch(
      patchExpenseAndOpenToastAndCloseFlyout(
        expense?.id,
        {
          ...changedFields,
          expenseReportId: expense?.expenseReportId,
        } as ExpenseFields,
        analytics,
      ),
    )
    trackUpdate()
  }

  useEffect((): void => {
    dispatch(getExpense(expenseId))
  }, [expense?.id])

  useEffect(() => {
    if (expense !== undefined) {
      dispatch(getExpenseReport(expense.expenseReportId))
    }
  }, [expense?.expenseReportId])

  useEffect((): void => {
    if (expense?.receiptAttachments !== undefined) {
      dispatch(
        createSetExpenseReceiptAttachmentIds(
          expense?.receiptAttachments.map((att) => att.id),
        ),
      )
    } else {
      dispatch(createSetExpenseReceiptAttachmentIds([]))
    }
  }, [expense?.receiptAttachments])

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {outOfPocketExpensesEnabled &&
      expenseReport !== undefined &&
      expense !== undefined &&
      canViewExpenses ? (
        <>
          <span>
            <ExpenseFlyoutHeader
              reportName={expenseReport.reportName}
              flyoutTitle={
                fieldsEditable || notesAndReceiptsEditable
                  ? 'Update Expense'
                  : 'Reimbursement'
              }
            />
            <ExpenseForm
              expenseReportId={expense.expenseReportId}
              expenseReportStatus={expenseReport.status}
              expense={expense}
              isSubmitter={isSubmitter}
              isApprover={isApprover}
              isManager={isManager}
              negativeButton={negativeButton}
              loading={patchingExpense}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              receiptAttachments={receiptAttachments}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              topLevelError={topLevelError}
              buttonText={translate('Update Expense')}
              baseFlatFields={baseFlatFields}
              notesAndReceiptsEditable={notesAndReceiptsEditable}
              fieldsEditable={fieldsEditable}
              mileageRate={organization?.mileageRateThousandths}
              onSuccessfulSubmit={dispatchPatchExpense}
            />
          </span>
        </>
      ) : loaded ? (
        <NotFoundRightFlyoutContent />
      ) : (
        <LoadingRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default UpdatExpenseFlyout
